import { useEffect, useRef, type DependencyList } from 'react';
import { overflowEdges } from './overflow-edges';

/**
 * Sets `data-overflow="none|top|bottom|both"` on a scroll container so CSS can
 * fade only the edges that actually have hidden content. Re-measures on scroll,
 * on container resize (a ResizeObserver — which also covers viewport resize,
 * since the container's flex height tracks the viewport), and whenever `deps`
 * change (pass the row count so connecting/disconnecting an agent re-measures —
 * content growth doesn't change the container's own size, so the observer alone
 * would miss it).
 */
export function useOverflowFade<T extends HTMLElement>(deps: DependencyList = []) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => { el.dataset.overflow = overflowEdges(el); };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}
